import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import { useAuth } from '@/auth/context'
import { ErrorState, FullPageLoading } from '@/ui/states'

/**
 * `/support-access#token=…` — where a staff member's support session becomes a
 * session on **this** host.
 *
 * Impersonation means using the customer's own application rather than a
 * reimplementation of it inside the staff console, so the borrowed session has to
 * be a cookie on `app.augrank.com`. The two hosts share no cookie by design
 * (REQUIREMENTS §9.1), so something has to cross between them, and it is a
 * single-use 256-bit token with a sixty-second life.
 *
 * **The token is read from the fragment, never from the query string.** A fragment
 * is not sent to a server: it cannot appear in a `Referer` header, in CloudFront's
 * access log, or in the API's own — and it is the one place a short-lived
 * credential in a URL is survivable. The staff console builds the link the same
 * way (`admin/src/pages/StartSupportSession.tsx`).
 *
 * **It is cleared from the address bar before the exchange is even attempted.**
 * `history.replaceState` runs first, so the token is out of the visible URL and out
 * of the browser's history entry regardless of what the API answers. It is spent
 * either way — the API's `UPDATE` claims it on the first redemption — so leaving it
 * on screen would only be a value somebody could paste into a support ticket.
 *
 * A `public_route`, necessarily: whoever arrives here is nobody on this host yet,
 * which is the entire purpose. What stands in for authentication is the token.
 */
export function SupportAccess(): JSX.Element {
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  // `useRef` rather than state: React 19's StrictMode mounts an effect twice in
  // development, and the token is single-use — the second attempt would report a
  // spent token over a session that had just been established correctly.
  const attempted = useRef(false)

  useEffect(() => {
    if (attempted.current) return
    attempted.current = true

    const token = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') ?? ''
    // Cleared first, and unconditionally. See the module docstring.
    window.history.replaceState(null, '', window.location.pathname)

    if (!token) {
      setFailure({
        code: 'UNAUTHENTICATED',
        message: 'That support-access link is missing its token. Start a new support session.',
        requestId: '',
        status: 401,
        retryAfterSeconds: null,
        fields: [],
        suggestion: null,
      })
      return
    }

    void (async () => {
      const result = await call(() =>
        api.POST('/api/v1/auth/impersonation/exchange', { body: { token } }),
      )
      if (!result.ok) {
        setFailure(result.failure)
        return
      }
      // Re-read the identity so the shell knows who it is acting as *and* draws
      // the support banner on the first paint of the screen it lands on.
      await refresh()
      void navigate('/', { replace: true })
    })()
  }, [navigate, refresh])

  if (failure) {
    return (
      <div className="app-full-state">
        <ErrorState failure={failure} title="This support link is no longer valid" />
        <p className="ar-subtle">
          A support-access link works once and expires in a minute. Ask the person who sent it to
          open a new session — which is another entry in the customer&apos;s audit log, and is meant
          to be.
        </p>
      </div>
    )
  }

  return <FullPageLoading label="Opening the support session" />
}
